<template>
  <div class="wrapper" :class="{'out-of-stock': !saleableQty}" v-touch="onSwipe">
    <div
      class="card-product-wrapper main"
      :class="{ shifted: isShifted }"
      :style="{ transform: `translateX(${currentShift}px)` }"
    >
      <div class="mobile-title-wrapper">
        <component
          :is="componentIs"
          :link="link ? link : ''"
          :class="{'out-of-stock': saleableQty === 0 }"
          class="title"
        >
          {{ title }}
        </component>
      </div>
      <div class="card-product">
        <div class="image-wrapper">
          <slot name="image" v-bind="{ image }">
            <img
              :src="image"
              :alt="alt"
              class="image"
              :class="{ 'is-cart-sidebar': isCartSidebar }"
            />
          </slot>
        </div>
        <div class="info-wrapper">
          <div class="details-price-wrapper">
            <div class="details-wrapper">
              <div class="title-wrapper">
                <component
                  :is="componentIs"
                  :link="link ? link : ''"
                  :class="{'title-sidebar': isCartSidebar }"
                  class="title"
                >
                  {{ title }}
                </component>
              </div>
              <div class="price-wrapper">
                <cart-price
                  :class="{ 'display-none': !regularPrice }"
                  :regular="regularPrice"
                  :special="specialPrice"
                  :discount="discount"
                />
              </div>
              <div class="configuration-wrapper">
                <slot name="configuration">
                  <SfProperty name="Size" value="XS" />
                  <SfProperty name="Color" value="white" />
                </slot>
              </div>
              <div v-if="isGiftWrapAvailable" class="giftwrap-wrapper">
                <SkeletonLoader
                  v-if="isGiftWrapAppliedLoading"
                  v-for="n in 1"
                  :key="n"
                  height="18px"
                  margin="8px 0 16px"
                />
                <SfCheckbox
                  v-if="!isGiftWrapAppliedLoading"
                  v-model="localIsGiftWrap"
                  label="Add Gift Packaging"
                  :disabled="!saleableQty"
                  rem-checkmark
                />
              </div>
            </div>
            <div class="price-wrapper">
              <cart-price
                :class="{ 'display-none': !regularPrice }"
                :regular="regularPrice"
                :special="specialPrice"
                :discount="discount"
              />
            </div>
          </div>
          <div class="qty-actions-wrapper" >
            <slot name="input">
              <div />
            </slot>
            <div class="actions-wrapper">
              <slot name="remove" v-bind="{ removeHandler }">
                <SfButton
                  aria-label="Remove"
                  class="remove"
                  :class="{ 'display-none': !hasRemove, 'show-remove': isCartSidebar }"
                  @click="removeHandler"
                >
                  {{ $t('Remove from bag') }}
                </SfButton>
              </slot>
              <div v-if="isShowAddToWishlist" class="dot" />
              <slot name="wishlist">
                <SfButton
                  v-if="isShowAddToWishlist"
                  aria-label="Wishlist toggle"
                  class="remove"
                  :class="{'added': isInWishlist}"
                  @click="addToWishlistHandler"
                >
                  {{ isInWishlist ? $t('Delete from Wishlist') : $t('Add to Wishlist') }}
                </SfButton>
              </slot>
              <div v-if="isGiftWrapAvailable" class="giftwrap-wrapper">
                <SkeletonLoader
                  v-if="isGiftWrapAppliedLoading"
                  v-for="n in 1"
                  :key="n"
                  height="18px"
                />
                <SfCheckbox
                  v-if="isShowAddGiftPackingInsideActionsBlock && !isGiftWrapAppliedLoading"
                  v-model="localIsGiftWrap"
                  class="giftwrap-wrapper"
                  label="Add Gift Packaging"
                  :disabled="!saleableQty"
                  rem-checkmark
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mobile-actions-wrapper" :class="{'mobile-actions-wrapper-sidebar': isCartSidebar}">
        <div class="actions-wrapper">
          <slot name="remove" v-bind="{ removeHandler }">
            <SfButton
              aria-label="Remove"
              class="remove"
              :class="{ 'display-none': !hasRemove, 'show-remove': isCartSidebar }"
              @click="removeHandler"
            >
              {{ $t('Remove from bag') }}
            </SfButton>
          </slot>
          <slot name="wishlist">
            <SfButton
              v-if="isShowAddToWishlist"
              aria-label="Wishlist toggle"
              class="remove"
              :class="{'added': isInWishlist}"
              @click="addToWishlistHandler"
            >
              {{ isInWishlist ? $t('Delete from Wishlist') : $t('Add to Wishlist') }}
            </SfButton>
          </slot>
          <div v-if="isGiftWrapAvailable" class="giftwrap-wrapper">
            <SkeletonLoader
              v-if="isGiftWrapAppliedLoading"
              v-for="n in 1"
              :key="n"
              height="18px"
            />
            <SfCheckbox
              v-if="!isGiftWrapAppliedLoading"
              v-model="localIsGiftWrap"
              label="Add Gift Packaging"
              :disabled="!saleableQty"
              rem-checkmark
            />
          </div>
        </div>
      </div>
      <div v-if="!isCartSidebar" class="notification-wrapper">
        <template v-if="notificationState === 'notifyMe'">
          <SfIcon icon="info_2025" size="18px" color="red" />
          <div class="notification">
            <SfButton pure class="notify-me-btn" @click="notificationHandler(true)">{{ notifyMeMessage }}</SfButton>
          </div>
        </template>

        <template v-else-if="notificationState === 'maxInStock'">
          <SfIcon icon="info_2025" size="18px" color="black" />
          <div class="notification"> {{ maxInStockMessage }}</div>
        </template>

        <template v-else-if="notificationState === 'notAvailable'">
          <SfIcon icon="info_2025" size="18px" color="red" />
          <div class="notification red">{{ notAvailableMessage }}</div>
        </template>

        <template v-else-if="notificationState === 'isNotReturnable'">
          <SfIcon icon="info_2025" size="18px" color="black" />
          <div class="notification"> {{ notReturnableMessage }}</div>
        </template>

        <template v-else-if="notificationState === 'limitedStock'">
          <SfIcon icon="info_2025" size="18px" color="black" />
          <div class="notification">
            <SfButton pure @click="notificationHandler('limitedStock')">{{ limitedStockMessage }}</SfButton>
          </div>
        </template>

      </div>
    </div>
    <div
      class="mobile-slide-wrapper actions"
      :class="{ visible: isShifted }"
      :style="{ transform: `translateX(${150 + currentShift}px)` }"
    >
      <SfButton
        v-if="isShowAddToWishlist"
        aria-label="Wishlist toggle"
        class="wishlist"
        :class="{ 'in-wish-list': isInWishlist }"
        @click="addToWishlistHandler"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="23" viewBox="0 0 17 23" fill="none">
          <path d="M8.5 14.3696L1 22.5V0.5H16V22.5L8.5 14.3696Z" stroke="white" stroke-linejoin="round" />
        </svg>
      </SfButton>
      <SfButton aria-label="Remove" class="delete" @click="removeHandler">
        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
          <path
            d="M12.5 19.2596L12.5 9.12019M1.51562 4.89543H4.05048M23.4844 4.89543H20.9495M16.7248 4.89543H8.27524M16.7248 4.89543V3.20553C16.7248 2.27222 15.9682 1.51562 15.0349 1.51562H9.96514C9.03184 1.51562 8.27524 2.27222 8.27524 3.20553V4.89543M16.7248 4.89543H20.9495M8.27524 4.89543H4.05048M20.9495 4.89543V20.1046C20.9495 21.9712 19.4363 23.4844 17.5697 23.4844H7.43029C5.56367 23.4844 4.05048 21.9712 4.05048 20.1046V4.89543"
            stroke="white" stroke-width="1.15625" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </SfButton>
    </div>
  </div>
</template>
<script>
import {
  SfIcon,
  SfButton,
  SfLink,
  SfProperty,
  SfQuantitySelector,
  SfCheckbox,
  SkeletonLoader
} from '~/components';

import { touch } from '~/utilities/directives';

export default {
  name: 'CartProduct',
  components: {
    SkeletonLoader,
    SfCheckbox,
    SfButton,
    SfIcon,
    SfQuantitySelector,
    SfLink,
    SfProperty,
    CartPrice: () => import('~/modules/checkout/components/cart/CartPrice.vue'),
  },
  directives: {touch},
  model: {
    prop: 'qty',
  },
  props: {
    image: {
      type: String,
      default: '',
    },
    alt: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    uid: {
      type: String,
      default: '',
    },
    sku: {
      type: String,
      default: '',
    },
    regularPrice: {
      type: [Number, String],
      default: null,
    },
    specialPrice: {
      type: [Number, String],
      default: null,
    },
    discount: {
      type: [Number, String],
      default: null,
    },
    qty: {
      type: [Number, String],
      default: 1,
    },
    saleableQty: {
      type: Number,
      default: 0,
    },
    minQty: {
      type: Number,
      default: null,
    },
    maxQty: {
      type: Number,
      default: null,
    },
    link: {
      type: [String, Object],
      default: null,
    },
    isInWishlist: {
      type: Boolean,
      default: false
    },
    hasRemove: {
      type: Boolean,
      default: true,
    },
    hasMoreActions: {
      type: Boolean,
      default: true,
    },
    isCartSidebar: {
      type: Boolean,
      default: false,
    },
    isShowAddToWishlist: {
      type: Boolean,
      default: true,
    },
    isShowAddGiftPacking: {
      type: Boolean,
      default: true,
    },
    isShowAddGiftPackingInsideActionsBlock: {
      type: Boolean,
      default: true,
    },
    isGiftWrapAvailable: {
      type: Boolean,
      default: false,
    },
    isGiftWrapApplied: {
      type: Boolean,
    },
    isGiftWrapAppliedLoading: {
      type: Boolean,
      default: false,
    },
    isProductAlertAvailable: {
      type: Boolean,
      default: false,
    },
    isReturnable: {
      type: Boolean,
    },
  },
  emits: [
    'input',
    'click:remove',
    'click:add-to-wishlist',
    'click:actions',
    'update:gift-wrap-applied'
  ],
  data() {
    return {
      currentShift: 0,
      maxShift: 150,
      isShifted: false,
      lastShift: 0,
      localIsGiftWrap: this.isGiftWrapApplied,
      notifyMeMessage: 'The product you’re looking for is currently unavailable. You can explore similar options or use “Notify Me” to...',
      notAvailableMessage: 'The product you\'re looking for is currently unavailable',
      limitedStockMessage: 'Limited stock available. Read more',
      notReturnableMessage: 'Please note: Sleeping pillows and duvets are non-returnable.',
      maxInStockMessage: `Due to limited availability, you can purchase up to ${this.saleableQty} units of this item per order.`
    };
  },
  computed: {
    componentIs() {
      return this.link ? 'SfLink' : 'div';
    },
    quantity() {
      return typeof this.qty === 'string' ? Number(this.qty) : this.qty;
    },
    notificationState() {
      if(!this.saleableQty && this.isProductAlertAvailable) {
        return 'notifyMe';
      }

      if(!this.saleableQty && !this.isProductAlertAvailable) {
        return 'notAvailable';
      }

      if(this.saleableQty === this.quantity && this.saleableQty !== 0 && this.quantity !== 0) {
        return 'maxInStock';
      }

      if(!this.isReturnable) {
        return 'isNotReturnable';
      }

      if(this.saleableQty < 5) {
        return 'limitedStock';
      }

      return null;
    }
  },
  watch: {
    localIsGiftWrap(newVal) {
      this.$emit('update:gift-wrap-applied', {status: newVal, uid: this.uid,});
    },
    isGiftWrapApplied(newVal) {
      this.localIsGiftWrap = newVal;
    },
  },
  methods: {
    removeHandler() {
      this.$emit('click:remove', true);
    },
    addToWishlistHandler() {
      this.$emit('click:add-to-wishlist');
    },
    actionsHandler() {
      this.$emit('click:actions');
    },
    notificationHandler(notification) {

      if(notification === 'isNotReturnable') {
        this.$emit('click:notification', 'isNotReturnable');
        return;
      }

      if(notification === 'limitedStock') {
        this.$emit('click:notification', 'limitedStock');
        return;
      }

      if(Number(this.saleableQty) <= 5) {
        this.$emit('click:notification', 6);
      } else {
        this.$emit('click:notification');
      }

      if(!this.saleableQty && this.isProductAlertAvailable) {
        this.$emit(
          'click:notification',
          {isShowNotifyMeSidebar: true, uid: this.uid, sku: this.sku});
      }
    },
    onSwipe(delta) {
      if(typeof delta === 'number') {
        let shift = delta;

        if(shift < -this.maxShift) shift = -this.maxShift;
        if(shift > 0) shift = 0;

        this.currentShift = shift;
        this.lastShift = shift;

      } else if(delta === 'end') {
        if(this.lastShift <= -this.maxShift / 2) {
          this.currentShift = -this.maxShift;
          this.isShifted = true;
        } else {
          this.currentShift = 0;
          this.isShifted = false;
        }
      }
    }
  },
};
</script>
<style lang="scss" scoped>
.wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 0 var(--sp_16);

  @include for-desktop($s) {
    padding: 0 var(--sp_24) 0 0;
  }

  @include for-desktop($t) {
    padding: 0;
  }
}

.main {
  position: relative;
  z-index: 1;
  transition: transform 0.3s ease;
  background: #fff;
  user-select: none;

  @include for-mobile() {
    cursor: pointer;
  }
}

.mobile-slide-wrapper {
  display: flex;
  cursor: pointer;

  &.actions {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 150px;
    z-index: 1;
    transform: translateX(100%);
    transition: transform 0.3s ease;
    pointer-events: none;

    &.visible {
      transform: translateX(0);
      pointer-events: auto;
    }
  }

  .wishlist, .delete {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 74px;
  }

  .wishlist {
    background-color: #907C4B;
  }

  .delete {
    background-color: #0F1319;
  }

  .in-wish-list {
    svg {
      fill: white;
    }
  }
}

.card-product-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--sp_12);
}

.mobile-title-wrapper {

  @include for-desktop($s) {
    display: none;
  }
}

.card-product {
  display: flex;
  gap: var(--sp_16);
}

.info-wrapper {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;

  .title-wrapper {
    display: none;

    @include for-desktop($s) {
      display: block;
    }
  }
}

.details-price-wrapper, .qty-actions-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--sp_16);
}

.details-price-wrapper {

  .details-wrapper {

    .price-wrapper {
      display: block;

      @include for-desktop($s) {
        display: none;
      }
    }
  }

  .price-wrapper {
    display: none;

    @include for-desktop($s) {
      display: block;
    }
  }

  .giftwrap-wrapper {
    display: none;

    @include for-desktop($ds) {
      display: block;
    }
  }
}

.qty-actions-wrapper {
  align-items: flex-end;

  .dot {

    @include for-desktop($ds) {
      display: inline-block;
      width: 2px;
      height: 2px;
      background-color: var(--c-grey-3);
      border-radius: 50%;
    }
  }
}

.image {
  width: 138px;
  min-width: 138px;
  aspect-ratio: 1;

  @include for-desktop($ds) {
    &:not(.is-cart-sidebar) {
      width: 200px;
      min-width: 200px;
    }
  }
}

.title {
  --link-text-decoration: none;
  display: block;
  font: 700 14px / 18px var(--font-family--primary);
  margin-bottom: 2px;

  @include for-desktop($t) {
    font: 700 16px / 18px var(--font-family--primary);
  }

  @include for-desktop($ds) {
    margin-bottom: 8px;
  }

  &-sidebar {
    font: 500 14px / 18px var(--font-family--primary);
  }
}

.sf-property__name,
.sf-property__value {
  font: 400 12px / 18px var(--font-family--primary);
  color: var(--c-grey-4);
}

.giftwrap-wrapper {
  --checkbox-size: 16px;
  --checkbox-font: 400 12px / 18px var(--font-family--primary);
  --checkbox-label-color: var(--c-grey-4);
  --checkbox-margin: 0;
  --checkbox-item-padding: 0;

  @include for-between($t, $ds) {
    display: none;
  }

  @include for-desktop($ds) {
    --checkbox-margin: var(--sp_8) 0 0 0;
  }

  ::v-deep {
    --checkbox-border-color: var(--c-grey-4);

    .sf-checkbox__label:hover {
      --checkbox-label-color: var(--c-grey-4);
    }

    .sf-checkbox__label--is-disabled {
      --checkbox-label-color: var(--c-grey-4);
    }

    .sf-checkbox__message {
      display: none;
    }

    &.is-disabled {
      .sf-checkbox__checkmark {
        --checkbox-border-color: var(--c-grey-4);
      }
    }
  }
}

.price-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.actions-wrapper {
  display: none;
  align-items: center;
  gap: 4px;
  --button-color: var(--c-grey-4);
  --button-padding: 0;

  @include for-desktop($s) {
    display: flex;
    align-items: center;
    gap: 4px;
    --button-color: var(--c-grey-4);
    --button-padding: 0;
  }

  .remove {
    font: 400 12px / 18px var(--font-family--primary);
    border-bottom: 1px solid var(--c-grey-4);

    @include for-between($s, $ds) {
      display: none;

      &.show-remove {
        display: block;
      }
    }
  }

  .added {
    color: var(--c-gold-2);
    border: none;
  }

  .giftwrap-wrapper {
    display: none;

    @include for-between($s, $ds) {
      display: block;
      width: 148px;
    }
  }
}

.mobile-actions-wrapper {
  display: flex;

  @include for-desktop($s) {
    display: none;
  }

  @include for-desktop($t) {
    display: flex;
  }

  @include for-desktop($ds) {
    display: none;
  }

  &-sidebar {
    display: none;
  }

  .actions-wrapper {
    display: flex;
    gap: var(--sp_8);

    .remove {
      display: none;

      @include for-desktop($t) {
        display: block;
      }
    }

    .giftwrap-wrapper {
      display: block;
      width: 148px;
      height: 18px;

      @include for-desktop($t) {
        display: none;
      }
    }
  }

  .remove {

    @include for-between($t, $ds) {
      display: block;
    }
  }
}

.notification-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  font: 400 14px / 18px var(--font-family--primary);

  .notification {
    display: flex;
    align-items: center;
    gap: 4px;

    &.red {
      color: var(--c-red);
    }
  }

  .sf-icon {
    min-width: 18px;
    align-self: flex-start;
  }

  .sf-button {
    --button-font: 400 14px / 18px var(--font-family--primary);
    --button-text-decoration: none;
  }

  .notify-me-btn {
    ::v-deep {
      --button-color: var(--c-red);
      --button-text-align: start;
    }
  }
}

.out-of-stock {
  .title {
    color: var(--c-grey-3);
  }

  .image {
    opacity: 0.6;
  }

  .qty-actions-wrapper {

    @include for-desktop($ds) {
      justify-content: center;
    }
  }
}
</style>
